import { useField } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { getNetworkErrorMessage, TableData } from '@framework/ui';
import { Alert } from 'antd';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useAsyncService } from '../../../../expressions/repository/async/useAsyncService';
import { useAsyncServiceGetData } from '../../../../expressions/repository/async/useAsyncServiceGetData';
import { useWidgetScopeContext } from '../../../../form/fillable/WidgetScopeContext';
import { useWidgetVisibility } from '../../../hooks/useWidgetVisibility';
import { IWidgetComponentProps } from '../../../IWidgetType';
import { useAsyncServiceTableColumns } from '../async-select/components/table/useAsyncServiceTableColumns';
import { asyncSelectWidgetObjectName } from '../async-select/hooks/asyncSelectObjectName';
import { getWidgetProps } from '../common/util/getWidgetProps';
import { IAsyncTableWidget } from './IAsyncTableWidget';

const StyledWrapper = styled.div`
  margin-bottom: 18px;
`;

export interface IAsyncTableWidgetComponentProps
  extends IWidgetComponentProps<IAsyncTableWidget> {}

export function AsyncTableWidgetComponent(
  props: IAsyncTableWidgetComponentProps,
) {
  const { widget } = props;

  const options = getWidgetProps(props);

  const { t } = useTranslation();
  const visible = useWidgetVisibility(props.widget, options);

  const { prefix } = useWidgetScopeContext();

  const { service, axiosClient } = useAsyncService(widget.serviceId);

  const { data, error, loading } = useAsyncServiceGetData({
    basePath: prefix,
    client: axiosClient,
    service,
    inputs: widget.inputs,
    disabled: options.readonly || !visible,
  });

  const fieldName = `${prefix}${asyncSelectWidgetObjectName(widget.code)}`;

  const value = useWatch({
    name: fieldName,
  }) as Array<Record<string, any>> | undefined;
  const field = useField(fieldName);

  const dataSource = useMemo(() => {
    if (options.readonly) {
      return value;
    }
    return data ?? [];
  }, [data, options.readonly, value]);

  useEffect(() => {
    if (options.readonly) {
      return;
    }
    if (error) return;
    if (loading) return;

    if (isEqual(dataSource, field.getValue())) return;

    field.setValue(dataSource, {
      shouldDirty: false,
      shouldValidate: false,
      shouldTouch: false,
    });
  }, [dataSource, error, field, loading, options.readonly]);

  const columns = useAsyncServiceTableColumns(widget);

  if (!visible) return null;

  // Nel caso, mostriamo un errore solo in fase di compilazione, non in readonly
  if (error && !options.readonly) {
    return (
      <Alert
        type="error"
        message={t(
          'widget.async-table-widget-error-title',
          'Errore durante il caricamento della tabella "{{widgetCode}}"',
          { widgetCode: widget.code },
        )}
        description={getNetworkErrorMessage(
          error,
          t(
            'widget.async-table-widget-error-description',
            'Impossibile visualizzare la tabella. Si è verificato un errore durante il caricamento dei dati. Riprovare',
          ),
        )}
        style={{
          marginBottom: '16px',
        }}
      />
    );
  }

  return (
    <StyledWrapper>
      <TableData
        dataSource={dataSource}
        size="small"
        loading={loading}
        columns={columns}
        darkTitleBar={false}
        pagination={false}
      />
    </StyledWrapper>
  );
}
